import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Globe from 'react-globe.gl';
import { destinations, destinationCategories } from '../data/destinations';
import HotelPartners from '../components/HotelPartners';

const DestinationsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredDestinations =
    selectedCategory === 'All'
      ? destinations
      : destinations.filter((d) => d.category === selectedCategory);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      data-testid="destinations-page"
    >
      {/* Hero */}
      <section
        className="relative h-[55vh] md:h-[65vh] flex items-center justify-center overflow-hidden"
        data-testid="destinations-hero"
      >
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1502602898657-3e91760cbb34?q=80&w=3840&auto=format&fit=crop"
            alt="Global luxury destinations"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black" />
        </div>

        <div className="relative z-10 container-padding text-center max-w-4xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="font-body text-xs uppercase tracking-[0.3em] text-accent mb-6"
          >
            Explore the World's Most Iconic Places
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="font-heading text-5xl md:text-7xl lg:text-8xl font-light tracking-tight leading-[0.9] text-primary"
            data-testid="destinations-title"
          >
            Destinations
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="font-body text-sm md:text-base text-muted-foreground max-w-2xl mx-auto mt-8 leading-relaxed"
          >
            A hand-picked edit of the world's most extraordinary places —
            heritage Indian icons and the most coveted international destinations.
          </motion.p>
        </div>
      </section>

      {/* Globe Section */}
      <section className="section-padding bg-black" data-testid="globe-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12 container-padding">
            <p className="font-body text-xs uppercase tracking-[0.3em] text-accent mb-4">
              Everywhere
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-light text-primary mb-6">
              Your World Awaits
            </h2>
          </div>

          <div className="h-[400px] md:h-[500px] flex items-center justify-center">
            <Globe
              globeImageUrl="//unpkg.com/three-globe/example/img/earth-night.jpg"
              backgroundColor="rgba(0,0,0,0)"
              width={typeof window !== 'undefined' ? Math.min(window.innerWidth * 0.8, 600) : 600}
              height={400}
              atmosphereColor="#D4AF37"
              atmosphereAltitude={0.15}
            />
          </div>
        </div>
      </section>

      {/* Category Filter + Destinations Grid */}
      <section
        className="section-padding container-padding"
        data-testid="destinations-grid-section"
      >
        <div className="max-w-[1920px] mx-auto">
          <div className="flex flex-wrap justify-center gap-3 md:gap-4 mb-16">
            {destinationCategories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                data-testid={`category-filter-${category
                  .toLowerCase()
                  .replace(' ', '-')}`}
                className={`px-6 py-3 text-xs uppercase tracking-widest font-body font-medium transition-all duration-300 ${
                  selectedCategory === category
                    ? 'bg-accent text-background'
                    : 'border border-primary/20 text-primary hover:border-accent hover:text-accent'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredDestinations.map((destination, index) => (
              <motion.div
                key={destination.name}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: (index % 9) * 0.08, duration: 0.6 }}
                className="destination-card group relative overflow-hidden aspect-[3/4] cursor-pointer"
                data-testid={`destination-${destination.name
                  .toLowerCase()
                  .replace(/\s+/g, '-')}`}
              >
                <img
                  src={destination.image}
                  alt={`${destination.name} — ${destination.landmark}`}
                  loading="lazy"
                  className="destination-card-image absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />

                <div className="absolute inset-0 p-8 flex flex-col justify-end">
                  <p className="font-body text-xs uppercase tracking-[0.2em] text-accent mb-2">
                    {destination.category}
                    <span className="text-primary/40 mx-2">·</span>
                    <span className="text-primary/60 normal-case tracking-normal">
                      {destination.landmark}
                    </span>
                  </p>
                  <h3 className="font-heading text-3xl md:text-4xl font-light text-primary mb-3">
                    {destination.name}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {destination.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Hotel Partners */}
      <HotelPartners variant="full" />
    </motion.div>
  );
};

export default DestinationsPage;
